/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useContext } from 'react';
import styled from 'styled-components';
import { AuthContext, ListingLabelContext } from 'contexts';
import { ROLES } from '@bb/users';

import { Button, Icon, Text, Dropdown, Menu, MenuItem, Tag } from 'components';

import { featureEnabled } from 'utils';
import FormContext from '../../FormContext';
import { transferTypeTagMap } from '../../../ManageUsersPage/Tables/TransferTags';
import { UserStatusTag } from '../../../../components';

const getViewListingsUrl = (userId: string) =>
  `/#/listing/list/brokerage/0?searchType=brokerage&status=All&location=All&avail=All&agent=${userId}&type=brokerage&pageNumber=0`;

const getViewMessagesUrl = (userId: string) => `/#/repinbox/${userId}`;

interface PostTitleProps {
  openHistoryModal: () => void;
}

const PostTitle = ({ openHistoryModal }: PostTitleProps) => {
  const { auth, offices } = useContext(AuthContext);

  const {
    isNew,
    isIncomingTransfer,
    isAnsweringOrMlsStaff,
    isOutgoingTransfer,
    isDeleted,
    userId,
    transfer,
    fetchedUserState,
    fetchedUserUnverified,
    userState,

    mapBoards,
  } = useContext(FormContext);

  const { getBoardName } = useContext(ListingLabelContext);

  if (isNew || isIncomingTransfer || !fetchedUserState) return null;

  const office = offices.find(
    (board: any) =>
      board &&
      board.address === fetchedUserState.address &&
      board.location === fetchedUserState.branch,
  );

  let boardText = '';
  if (fetchedUserState.memberNumbers[0]?.board === 'NO_BOARD') {
    boardText = `No Board: ${fetchedUserState.memberNumbers[0].number}`;
  } else {
    const memberNumber = fetchedUserState?.memberNumbers.find(
      ({ isPrimary }) => isPrimary,
    );
    boardText = [
      office?.mls,
      getBoardName(mapBoards(memberNumber?.board), false),
      office?.oid,
      // Display keyholderId (mapped mlsUniqueId) instead of mlsNumber for data connect users
      featureEnabled('membershipKeyholderIdSupportFeature', 1) &&
      fetchedUserState.usingDataConnectAPI &&
      memberNumber?.mlsUniqueId
        ? memberNumber?.mlsUniqueId
        : memberNumber?.number,
    ]
      .filter(Boolean)
      .join(' | ');
  }

  let transferTag;
  if (isDeleted) {
    transferTag = (
      <>
        <Tag color="red">Deleted</Tag>
        {transfer?.type && transferTypeTagMap[transfer.type]}
      </>
    );
  } else if (isIncomingTransfer || isOutgoingTransfer) {
    transferTag = <Tag color="orange">Pending</Tag>;
  } else {
    transferTag = (
      <UserStatusTag
        disabled={userState.disabled}
        isUnverified={fetchedUserUnverified}
        inline
      />
    );
  }

  const menuItems: React.ReactNode[] = [];

  if (ROLES[auth.role] >= ROLES.BRANCH_ADMIN) {
    menuItems.push(
      <MenuItem
        key="history"
        data-cy="title-dropdown-history"
        onClick={openHistoryModal}
      >
        View User History
      </MenuItem>,
    );
  }

  if (isDeleted && !isAnsweringOrMlsStaff) {
    menuItems.push(
      <MenuItem
        key="listings"
        data-cy="title-dropdown-listings"
        href={getViewListingsUrl(userId)}
      >
        View Listings
      </MenuItem>,
      <MenuItem
        key="messages"
        data-cy="title-dropdown-messages"
        href={getViewMessagesUrl(userId)}
      >
        View Messages
      </MenuItem>,
    );
  }

  return (
    <div>
      {transferTag}
      {transferTag && boardText && <HorizontalDivider />}
      <TitleBoardLabel>{boardText}</TitleBoardLabel>
      {menuItems.length > 0 && (
        <DropdownContainer data-cy="title-dropdown">
          <Dropdown
            trigger={['click']}
            getPopupContainer={el => el.parentElement}
            dropdownRender={() => <Menu>{menuItems}</Menu>}
          >
            <Button type="text" icon={<Icon type="ellipsis" width={25} />} />
          </Dropdown>
        </DropdownContainer>
      )}
    </div>
  );
};

export default PostTitle;

const TitleBoardLabel = styled(Text)`
  color: black;
  opacity: 45%;
`;

const HorizontalDivider = styled.span`
  display: inline-block;
  background-color: #f0f0f0;
  width: 1px;
  height: 12px;
  margin-right: 8px;
  margin-left: 2px;
`;

const DropdownContainer = styled.div`
  margin-left: auto;
`;
